import { useEffect, useState, useCallback, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ClipboardList, Plus, ArrowLeft, Search, Sparkles, X, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { enablePush, pushSupported, playBeep } from "@/lib/push";
import { TaskChat } from "./TaskChat";

export const dueInfo = (due_at, status) => {
  if (status === "closed") return { top: "DONE", bottom: "", cls: "bg-slate-200 text-slate-600" };
  if (!due_at) return { top: "—", bottom: "no due", cls: "bg-slate-100 text-slate-500" };
  const due = new Date(due_at);
  const now = new Date();
  const diffMs = due - now;
  const hrs = Math.abs(diffMs) / 36e5;
  const t = due.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (diffMs < 0) {
    const d = Math.floor(hrs / 24);
    return { top: "OVER", bottom: d >= 1 ? `${d}d` : `${Math.floor(hrs)}h`, cls: "bg-rose-600 text-white" };
  }
  if (due.toDateString() === now.toDateString()) return { top: "TODAY", bottom: t, cls: "bg-amber-500 text-white" };
  const d = Math.ceil(hrs / 24);
  return { top: "IN", bottom: `${d}d`, cls: "bg-emerald-600 text-white" };
};

const FILTERS = [["all", "All"], ["assigned", "To me"], ["created", "By me"], ["overdue", "Overdue"], ["closed", "Closed"]];
const DUE_CHIPS = [
  ["Today 6 PM", () => { const d = new Date(); d.setHours(18, 0, 0, 0); return d; }],
  ["Tomorrow 10 AM", () => { const d = new Date(Date.now() + 864e5); d.setHours(10, 0, 0, 0); return d; }],
  ["In 2 days", () => { const d = new Date(Date.now() + 2 * 864e5); d.setHours(10, 0, 0, 0); return d; }],
];

export const TaskForm = ({ people, initial, submitLabel = "Create Task", onSubmit, onBack }) => {
  const [headline, setHeadline] = useState(initial?.headline || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [sel, setSel] = useState(() => Object.fromEntries((initial?.assignees || []).map((a) => [a.email, a])));
  const [due, setDue] = useState(initial?.due_at ? new Date(initial.due_at) : null);
  const [noDue, setNoDue] = useState(initial ? !initial.due_at : false);
  const [aiFu, setAiFu] = useState(initial ? initial.ai_followup !== false : true);
  const [busy, setBusy] = useState(false);
  const toggle = (email, name) => setSel((s) => {
    const n = { ...s };
    if (!n[email]) n[email] = { email, name, role: "primary" };
    else if (n[email].role === "primary") n[email] = { ...n[email], role: "secondary" };
    else delete n[email];
    return n;
  });
  const submit = async () => {
    const assignees = Object.values(sel);
    if (!headline.trim()) return toast.error("Headline required");
    if (!assignees.length) return toast.error("Assign to at least one person");
    setBusy(true);
    try {
      await onSubmit({ headline, description: desc, assignees, due_at: noDue || !due ? null : due.toISOString(), clear_due: noDue || !due, ai_followup: aiFu });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white text-slate-800">
      <button data-testid="newtask-back" onClick={onBack} className="flex items-center gap-1 text-blue-600 text-sm font-semibold"><ArrowLeft className="h-4 w-4" /> Back</button>
      <Input data-testid="newtask-headline" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Task headline *" className="bg-white border-slate-300" />
      <textarea data-testid="newtask-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" rows={3}
        className="w-full rounded-md border border-slate-300 p-2.5 text-sm" />
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase mb-1.5">Assign to (tap: Primary → Secondary → off)</p>
        <div className="flex flex-wrap gap-1.5">
          {people.map((p) => {
            const s = sel[p.email];
            return (
              <button key={p.email} data-testid={`assignee-${p.email}`} onClick={() => toggle(p.email, p.name)}
                className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  !s ? "border-slate-300 text-slate-600 bg-white" :
                  s.role === "primary" ? "border-blue-600 bg-blue-600 text-white" : "border-blue-400 bg-blue-100 text-blue-700 border-dashed"}`}>
                {p.name}{s ? ` · ${s.role === "primary" ? "P" : "S"}` : ""}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase mb-1.5">Due date & time</p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {DUE_CHIPS.map(([l, f]) => (
            <button key={l} data-testid={`due-chip-${l}`} onClick={() => { setDue(f()); setNoDue(false); }}
              className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border ${due && !noDue && f().getTime() === due.getTime() ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 text-slate-600"}`}>{l}</button>
          ))}
          <button data-testid="due-chip-none" onClick={() => { setNoDue(true); setDue(null); }}
            className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border ${noDue ? "bg-slate-700 text-white border-slate-700" : "border-slate-300 text-slate-600"}`}>No due date</button>
        </div>
        <input data-testid="due-datetime" type="datetime-local" className="rounded-md border border-slate-300 p-2 text-sm w-full"
          value={due ? new Date(due.getTime() - due.getTimezoneOffset() * 6e4).toISOString().slice(0, 16) : ""}
          onChange={(e) => { if (e.target.value) { setDue(new Date(e.target.value)); setNoDue(false); } }} />
      </div>
      <div className="flex items-center justify-between rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5">
        <div>
          <p className="text-sm font-semibold text-violet-800 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> AI Follow-up</p>
          <p className="text-[10px] text-violet-600">Aggressive: once at 24h before due · every 4h when overdue</p>
        </div>
        <Switch data-testid="newtask-aifu" checked={aiFu} onCheckedChange={setAiFu} />
      </div>
      <Button data-testid="newtask-create" onClick={submit} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 text-white">{submitLabel}</Button>
    </div>
  );
};

const NewTask = ({ people, onDone, onBack, initial }) => (
  <TaskForm people={people} onBack={onBack} initial={initial}
    onSubmit={async (payload) => {
      await api.post("/tasks", { headline: payload.headline, description: payload.description,
        assignees: payload.assignees, due_at: payload.due_at, ai_followup: payload.ai_followup });
      toast.success("Task created");
      onDone();
    }} />
);

export const TasksPanel = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [people, setPeople] = useState([]);
  const [current, setCurrent] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [badge, setBadge] = useState(0);
  const prevBadge = useRef(-1);
  const [notifOn, setNotifOn] = useState(() => pushSupported() && Notification.permission === "granted");

  const loadBadge = useCallback(() => api.get("/tasks/unread").then((r) => {
    setBadge(r.data.total);
    if (prevBadge.current >= 0 && r.data.total > prevBadge.current) {
      playBeep();
      toast.info("📋 New task activity", { description: "Open Tasks to see the latest messages" });
    }
    prevBadge.current = r.data.total;
  }).catch(() => {}), []);
  const loadTasks = useCallback((f) => api.get(`/tasks?view=${f}`).then((r) => setTasks(r.data)).catch(() => {}), []);

  useEffect(() => { loadBadge(); const t = setInterval(loadBadge, 30000); return () => clearInterval(t); }, [loadBadge]);
  useEffect(() => {
    const h = () => { setOpen(true); setView("list"); };
    const hNew = (e) => { setPrefill(e.detail || null); setOpen(true); setView("new"); };
    window.addEventListener("open-tasks", h);
    window.addEventListener("open-tasks-new", hNew);
    return () => { window.removeEventListener("open-tasks", h); window.removeEventListener("open-tasks-new", hNew); };
  }, []);
  useEffect(() => {
    if (pushSupported() && Notification.permission === "granted") enablePush().catch(() => {});
  }, []);
  const askNotif = async () => {
    const r = await enablePush().catch(() => ({ ok: false, reason: "error" }));
    if (r.ok) { setNotifOn(true); toast.success("Notifications enabled on this device"); }
    else if (r.reason === "denied") toast.error("Notifications blocked — allow them in browser site settings");
    else if (r.reason === "unsupported") toast.error("This browser doesn't support notifications. On iPhone, add the site to Home Screen first.");
    else toast.error("Could not enable notifications");
  };
  useEffect(() => {
    if (!open) return;
    loadTasks(filter);
    api.get("/tasks/people").then((r) => setPeople(r.data)).catch(() => {});
    const t = setInterval(() => view === "list" && loadTasks(filter), 15000);
    return () => clearInterval(t);
  }, [open, filter, view, loadTasks]);

  const rows = tasks.filter((t) => !q || t.headline.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <button data-testid="tasks-fab" onClick={() => { setOpen(true); setView("list"); }} title="Tasks"
        className="fixed right-0 top-[calc(50%+115px)] -translate-y-1/2 z-40 bg-blue-600 hover:bg-blue-500 text-white rounded-l-xl shadow-lg px-2.5 py-4 flex flex-col items-center gap-1 transition-colors">
        <span className="relative">
          <ClipboardList className="h-5 w-5" />
          {badge > 0 && <span data-testid="tasks-fab-badge" className="absolute -top-2 -right-2 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-0.5 flex items-center justify-center">{badge}</span>}
        </span>
        <span className="text-[9px] font-bold tracking-wider" style={{ writingMode: "vertical-rl" }}>TASKS</span>
      </button>
      <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setView("list"); loadBadge(); } }}>
        <SheetContent side="right" data-testid="tasks-panel" className="w-full sm:max-w-md bg-white border-slate-200 p-0 flex flex-col gap-0">
          <SheetHeader className="px-4 py-3 bg-blue-700 space-y-0">
            <SheetTitle className="text-white flex items-center justify-between text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
              <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Tasks</span>
              <span className="flex items-center gap-1.5 mr-6">
                {!notifOn && (
                  <Button data-testid="tasks-notif-btn" size="sm" onClick={askNotif} title="Enable notifications"
                    className="h-8 px-2 rounded-full bg-blue-600 text-white hover:bg-blue-500 text-[10px] font-bold">
                    <Bell className="h-3.5 w-3.5 mr-1" /> Enable alerts
                  </Button>
                )}
                {view === "list" && (
                  <Button data-testid="tasks-new-btn" size="sm" onClick={() => setView("new")}
                    className="h-8 w-8 p-0 rounded-full bg-white text-blue-700 hover:bg-blue-50"><Plus className="h-4 w-4" /></Button>
                )}
              </span>
            </SheetTitle>
            <SheetDescription className="sr-only">Task list and chat</SheetDescription>
          </SheetHeader>
          {view === "new" && <NewTask people={people} initial={prefill} onBack={() => { setPrefill(null); setView("list"); }} onDone={() => { setPrefill(null); setView("list"); loadTasks(filter); }} />}
          {view === "chat" && current && (
            <TaskChat taskId={current} people={people} onBack={() => { setView("list"); loadTasks(filter); loadBadge(); }} />
          )}
          {view === "list" && (
            <div className="flex-1 flex flex-col overflow-hidden bg-white">
              <div className="px-3 py-2 border-b border-slate-100 space-y-2">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                  <Input data-testid="tasks-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…"
                    className="h-8 pl-8 bg-slate-50 border-slate-200 text-slate-800 text-sm" />
                </div>
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
                  {FILTERS.map(([v, l]) => (
                    <button key={v} data-testid={`tasks-filter-${v}`} onClick={() => setFilter(v)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${filter === v ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                {rows.map((t) => {
                  const d = dueInfo(t.due_at, t.status);
                  return (
                    <button key={t.id} data-testid={`task-row-${t.id}`} onClick={() => { setCurrent(t.id); setView("chat"); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50/60 text-left transition-colors">
                      <span className={`shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center ${d.cls}`}>
                        <span className="text-[9px] font-bold leading-tight">{d.top}</span>
                        <span className="text-[10px] font-extrabold leading-tight">{d.bottom}</span>
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1">
                          <span className="font-semibold text-sm text-slate-800 truncate">{t.headline}</span>
                          {t.ai_followup && t.status !== "closed" && <Sparkles className="h-3 w-3 text-violet-500 shrink-0" />}
                        </span>
                        <span className="block text-xs text-slate-500 truncate">{t.last_message_preview || t.description || "No messages yet"}</span>
                      </span>
                      <span className="shrink-0 flex flex-col items-end gap-1">
                        <span className="text-[10px] text-slate-400">{t.last_activity_at ? new Date(t.last_activity_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}</span>
                        {t.unread > 0 && <span data-testid={`task-unread-${t.id}`} className="bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{t.unread}</span>}
                      </span>
                    </button>
                  );
                })}
                {!rows.length && <p className="text-center text-sm text-slate-400 py-10">No tasks here. Tap + to create one.</p>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
